import * as React from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Button,
  Form,
  FormField,
  FormLabel,
  FormItem,
  FormControl,
  FormMessage,
  Input,
  PasswordInput,
  cn,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  emailLabel,
  emailPlaceholder,
  emailRequiredValidationMessage,
  forgotPasswordText,
  passwordLabel,
  passwordRequiredValidationMessage,
  signInButtonLabel,
} from '../../../../../stringsManager';
import { MailIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

const formSchema = z.object({
  email: z.string().min(1, { message: emailRequiredValidationMessage }).email(),
  password: z
    .string({ required_error: passwordRequiredValidationMessage })
    .min(1),
});

interface SignInFormProps {
  onSubmit: (values: z.infer<typeof formSchema>) => void;
  loading: boolean;
  className?: string;
  resetPasswordRoute: string;
  defaultValues?: Partial<z.infer<typeof formSchema>>;
  searchParams: URLSearchParams;
}

export const SignInForm = ({
  className,
  loading,
  onSubmit,
  resetPasswordRoute,
  defaultValues,
  searchParams,
  ...props
}: SignInFormProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: defaultValues?.email,
      password: defaultValues?.password,
    },
  });

  return (
    <div className={cn('grid gap-4 text-foreground', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-normal">{emailLabel}</FormLabel>
                <FormControl>
                  <Input
                    disabled={loading}
                    placeholder={emailPlaceholder}
                    {...field}
                    endAdornment={<MailIcon />}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-normal">{passwordLabel}</FormLabel>
                <FormControl>
                  <PasswordInput disabled={loading} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end">
            <Link
              to={resetPasswordRoute}
              className="text-sm text-primary leading-tight"
            >
              {forgotPasswordText}
            </Link>
          </div>

          <Button disabled={loading} type="submit" className="w-full">
            {signInButtonLabel}
          </Button>
        </form>
      </Form>
    </div>
  );
};
